/**
 * Phase 10: PoW Note Forwarding Service
 *
 * Automatically forwards PoW notes discovered from other relays to the
 * notemine.io relay. Silent, batched, best-effort.
 */

import type { NostrEvent } from 'nostr-tools/core';
import { relayPool, DEFAULT_POW_RELAY } from '../lib/applesauce';
import { hasValidPow } from '../lib/pow';

// PoW thresholds by event type (aligned with defaults.ts minPow* values)
const POW_THRESHOLD_REACTION = 21;   // kind 7
const POW_THRESHOLD_REPLY = 18;      // kind 1 with 'e' tags
const POW_THRESHOLD_ROOT_NOTE = 16;  // kind 1 without 'e' tags

const BATCH_WINDOW_MS = 2000;
const MAX_BATCH_SIZE = 10;
const PUBLISH_TIMEOUT_MS = 5000;
const MAX_FORWARDED_IDS = 10_000;

class PowForwardingServiceImpl {
  private forwardedIds = new Set<string>();
  private pendingBatch: NostrEvent[] = [];
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private userPubkey: string | null = null;
  private debugMode = false;

  configure(userPubkey: string, debugMode: boolean = false): void {
    this.userPubkey = userPubkey;
    this.debugMode = debugMode;
    if (this.debugMode) {
      console.log('[PowForwarding] Configured for user', userPubkey.slice(0, 8));
    }
  }

  /**
   * Check if an event should be forwarded and queue it if so.
   * Call this after any event is accepted into the feed.
   */
  maybeForward(event: NostrEvent, sourceRelayUrl?: string): void {
    if (!this.shouldForward(event, sourceRelayUrl)) return;

    this.forwardedIds.add(event.id);
    this.pendingBatch.push(event);

    if (this.debugMode) {
      console.log(`[PowForwarding] Queued event ${event.id.slice(0, 8)} kind=${event.kind} (batch: ${this.pendingBatch.length})`);
    }

    if (this.pendingBatch.length >= MAX_BATCH_SIZE) {
      this.flush();
    } else if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => this.flush(), BATCH_WINDOW_MS);
    }
  }

  private shouldForward(event: NostrEvent, sourceRelayUrl?: string): boolean {
    // Already forwarded
    if (this.forwardedIds.has(event.id)) return false;

    // User's own events — already published to notemine.io
    if (this.userPubkey && event.pubkey === this.userPubkey) return false;

    // Already on notemine.io
    if (sourceRelayUrl === DEFAULT_POW_RELAY) return false;

    // Only kind 1 and kind 7
    if (event.kind !== 1 && event.kind !== 7) return false;

    // Determine threshold by type
    let threshold: number;
    if (event.kind === 7) {
      threshold = POW_THRESHOLD_REACTION;
    } else {
      // kind 1: check if reply (has 'e' tags) or root note
      const isReply = event.tags.some(t => t[0] === 'e');
      threshold = isReply ? POW_THRESHOLD_REPLY : POW_THRESHOLD_ROOT_NOTE;
    }

    return hasValidPow(event, threshold);
  }

  private flush(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    const batch = this.pendingBatch.splice(0);
    if (batch.length === 0) return;

    if (this.debugMode) {
      console.log(`[PowForwarding] Flushing ${batch.length} events to ${DEFAULT_POW_RELAY}`);
    }

    // Best-effort publish with result logging
    const publishPromises = batch.map(event =>
      Promise.race([
        relayPool.relay(DEFAULT_POW_RELAY).publish(event).then((result) => {
          if (this.debugMode) {
            console.log(`[PowForwarding] Published ${event.id.slice(0, 8)} kind=${event.kind}:`, result);
          }
          return result;
        }).catch((err) => {
          // Always log publish failures — silent failures mask broken forwarding
          console.warn(`[PowForwarding] Publish failed ${event.id.slice(0, 8)} kind=${event.kind}:`, err?.message || err);
        }),
        new Promise<void>((resolve) => setTimeout(resolve, PUBLISH_TIMEOUT_MS)),
      ])
    );

    Promise.allSettled(publishPromises).then((results) => {
      if (this.debugMode) {
        const fulfilled = results.filter(r => r.status === 'fulfilled').length;
        console.log(`[PowForwarding] Batch complete: ${fulfilled}/${batch.length} published`);
      }
    });

    // Cap memory usage
    if (this.forwardedIds.size > MAX_FORWARDED_IDS) {
      if (this.debugMode) {
        console.log(`[PowForwarding] Clearing forwarded IDs cache (was ${this.forwardedIds.size})`);
      }
      this.forwardedIds.clear();
    }
  }

  destroy(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    this.pendingBatch = [];
    this.forwardedIds.clear();
    this.userPubkey = null;
  }
}

// Singleton
let globalService: PowForwardingServiceImpl | null = null;

export function getPowForwardingService(): PowForwardingServiceImpl {
  if (!globalService) {
    globalService = new PowForwardingServiceImpl();
  }
  return globalService;
}

export function configurePowForwarding(userPubkey: string, debugMode: boolean = false): void {
  getPowForwardingService().configure(userPubkey, debugMode);
  // Expose on window in dev for console debugging
  if (import.meta.env.DEV) {
    (window as any).__powForwarding = getPowForwardingService();
  }
}

export function destroyPowForwardingService(): void {
  if (globalService) {
    globalService.destroy();
    globalService = null;
  }
}
